import axios, { AxiosRequestConfig } from 'axios';
import { getOdataRootUrl } from './utils';
import { convertToCampaignServerContext } from './server-context-utils';
import { IsCopilotRegionRestrictedParams } from './types';

const COPILOT_RESTRICTED_REGION_PARAM = 'restrictedregion';
const DEFAULT_REGION_RESTRICTION = false;

function fetchIsCopilotEnabled(url: string, Authorization: string) {
  const config: AxiosRequestConfig = {
    headers: {
      Authorization,
    },
  };

  return axios.post(url, undefined, config);
}

// cache the value for each URL
const cache: { [key: string]: Promise<boolean> } = {};

/**
 * Checks if the current account is in a region where Copilot should be disabled.
 * @param permissions {Object} - Permissions object.
 * @param feature {string} - The Copilot feature to check for region restrictions.
 * @returns {Promise<boolean>} - True if the account is in a region where Copilot should be disabled.
 */
export function isCopilotRegionRestricted(params: IsCopilotRegionRestrictedParams): Promise<boolean> {
  const {
    feature,
    logger,
    permissions,
    search = window.location.search,
    serverContext,
  } = params;

  if (permissions?.dynamic?.AdsCopilotRegionRestriction === true) {
    const searchParams = new URLSearchParams(search);

    // allow overriding region check for testing purposes
    const restrictedRegionParamValue = searchParams.get(COPILOT_RESTRICTED_REGION_PARAM);
    if (restrictedRegionParamValue === '1') {
      return Promise.resolve(true);
    }
    if (restrictedRegionParamValue === '0') {
      return Promise.resolve(false);
    }

    const campaignServerContext = convertToCampaignServerContext(serverContext);
    if (campaignServerContext) {
      // get cid and aid for URL
      const customerId = campaignServerContext.CurrentAccount?.AdvertiserCustomerID || campaignServerContext.CurrentCustomer?.Id;
      const accountId = campaignServerContext.CurrentAccount?.Id;

      if (customerId !== undefined && customerId !== null) {
        const urlObject = new URL(`${getOdataRootUrl()}/ODataApi/Campaign/V2/CopilotEnabledCheck`);
        urlObject.searchParams.set('customerId', customerId.toString());
        if (accountId) {
          urlObject.searchParams.set('accountId', accountId.toString());
        }

        const url = urlObject.toString();

        if (cache[url] === undefined && campaignServerContext.Token) {
          // create a new promise and cache it
          cache[url] = fetchIsCopilotEnabled(url, `CCMTSmallToken ${campaignServerContext.Token}`)
            .then((response) => {
              const isCopilotEnabled = response.data?.IsCopilotEnabled;
              const requestId = response.headers?.['x-ms-requestid'];

              if (isCopilotEnabled === undefined || isCopilotEnabled === null) {
                // data is not available in response
                logger?.trace(`isCopilotRegionRestricted: No data received from fetchIsCopilotEnabled. requestId="${requestId}", isRestricted=${DEFAULT_REGION_RESTRICTION}, feature=${feature}, url=${url}, status=${response.status}`);
                return DEFAULT_REGION_RESTRICTION;
              }

              const isRestricted = isCopilotEnabled !== true;
              logger?.trace(`isCopilotRegionRestricted: requestId="${requestId}", isRestricted=${isRestricted}, feature=${feature}, url=${url}, status=${response.status}`);
              return isRestricted;
            }).catch((e) => {
              const requestId = e?.response?.headers?.['x-ms-requestid'];
              logger?.trace(`isCopilotRegionRestricted: Error during fetchIsCopilotEnabled, defaulting to ${DEFAULT_REGION_RESTRICTION}. requestId="${requestId}", status=${e?.response?.status} e="${e}"`);

              return DEFAULT_REGION_RESTRICTION;
            });
        }

        if (cache[url] !== undefined) {
          // return cached value if available
          return cache[url];
        }
      }
    }

    logger?.trace(`isCopilotRegionRestricted: Could not get URL or cache for region check, defaulting to ${DEFAULT_REGION_RESTRICTION}`);
    return Promise.resolve(DEFAULT_REGION_RESTRICTION);
  }

  // pilot not enabled
  return Promise.resolve(false);
}
